import React, { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { DXTRADE_POSITION_EFFECT, DxtradePositionEffect } from '@pages/Trade/utils/constants'

import { Button } from '@ui/components/Button'
import { Chip } from '@ui/components/Chip'
import { ListItem } from '@ui/components/List'
import { Popover, PopoverCard, PopoverList } from '@ui/components/Popover'
import { ChevronIcon } from '@ui/icons/Chevron.icon'
import { CircleCheckIcon } from '@ui/icons/CircleCheck.icon'

import type { DxTradeOrderType } from '@model/DxTrade.types'

import styles from '../Portfolio.page.module.scss'

interface PortfolioFiltersProps {
  includeOrderType?: boolean
  onDateFilterClick?: () => void
  includeEffectFilter?: boolean
  searchFilters?: {
    side?: 'BUY' | 'SELL'
    ofType?: DxTradeOrderType
    positionEffect?: DxtradePositionEffect
  }
  setSearchFilters?: (value: {
    side?: 'BUY' | 'SELL'
    ofType?: DxTradeOrderType | undefined
    positionEffect?: DxtradePositionEffect
  }) => void
}

export const PortfolioFilters: React.FC<PortfolioFiltersProps> = ({
  includeOrderType,
  includeEffectFilter,
  searchFilters,
  setSearchFilters,
}) => {
  const { t } = useTranslation()

  const tradeTypeOptions = includeOrderType
    ? [
        { id: 'all', name: t('All types') },
        { id: 'BUY-MARKET', name: t('Buy Market') },
        { id: 'BUY-LIMIT', name: t('Buy Limit') },
        { id: 'BUY-STOP', name: t('Buy Stop') },
        { id: 'SELL-MARKET', name: t('Sell Market') },
        { id: 'SELL-LIMIT', name: t('Sell Limit') },
        { id: 'SELL-STOP', name: t('Sell Stop') },
      ]
    : [
        { id: 'all', name: t('All types') },
        { id: 'BUY', name: t('Buy') },
        { id: 'SELL', name: t('Sell') },
      ]

  const [isTypeDropdownOpen, setIsTypeDropdownOpen] = useState(false)
  const [isEffectDropdownOpen, setIsEffectDropdownOpen] = useState(false)

  const effectOptions: { id: DxtradePositionEffect; name: string }[] = [
    { id: DXTRADE_POSITION_EFFECT.OPEN, name: t('Portfolio.Opened') },
    { id: DXTRADE_POSITION_EFFECT.CLOSE, name: t('Portfolio.Closed') },
  ]

  const handleTypeSelect = useCallback(
    (selectedId: string) => {
      if (!includeOrderType) {
        setSearchFilters?.({
          side: selectedId === 'all' ? undefined : (selectedId as 'BUY' | 'SELL'),
          ofType: undefined,
        })
      } else {
        if (selectedId === 'all') {
          setSearchFilters?.({ side: undefined as any, ofType: undefined })
        } else {
          const [side, rawType] = selectedId.split('-')
          setSearchFilters?.({ side: side as 'BUY' | 'SELL', ofType: rawType as DxTradeOrderType })
        }
      }
      setIsTypeDropdownOpen(false)
    },
    [includeOrderType, setSearchFilters]
  )

  const handleEffectSelect = useCallback(
    (selectedId: DxtradePositionEffect) => {
      if (searchFilters?.positionEffect === selectedId) {
        setIsEffectDropdownOpen(false)
        return
      }
      setSearchFilters?.({ positionEffect: selectedId })
      setIsEffectDropdownOpen(false)
    },
    [searchFilters?.positionEffect, setSearchFilters]
  )

  return (
    <div className={styles.chips}>
      <Popover
        isOpen={isTypeDropdownOpen}
        setIsOpen={setIsTypeDropdownOpen}
        label={t('Portfolio.Trade type')}
        content={
          <PopoverCard>
            <PopoverList>
              {tradeTypeOptions.map((option) => {
                const selectedId = includeOrderType
                  ? searchFilters?.side && searchFilters?.ofType
                    ? `${searchFilters.side}-${searchFilters.ofType}`.toUpperCase()
                    : 'all'
                  : searchFilters?.side ?? 'all'

                const isActive = option.id === selectedId

                return (
                  <ListItem
                    key={option.id}
                    onClick={() => handleTypeSelect(option.id)}
                    active={isActive}
                    endSlot={
                      isActive
                        ? {
                            icon: {
                              icon: { icon: CircleCheckIcon, color: 'text-purple-high-em' },
                            },
                          }
                        : undefined
                    }
                  >
                    {option.name}
                  </ListItem>
                )
              })}
            </PopoverList>
          </PopoverCard>
        }
      >
        <Button.Secondary
          size='md'
          iconRight={ChevronIcon.Down}
          onClick={() => setIsTypeDropdownOpen(!isTypeDropdownOpen)}
          endDecorator={
            ((includeOrderType && searchFilters?.side && searchFilters?.ofType) ||
              (!includeOrderType && searchFilters?.side)) && (
              <Chip
                selected
                size='xs'
                onDelete={(event) => {
                  event.stopPropagation()
                  event.preventDefault()
                  setSearchFilters?.({ side: undefined, ofType: undefined })
                }}
              >
                {'1'}
              </Chip>
            )
          }
        >
          {t('Type')}
        </Button.Secondary>
      </Popover>

      {includeEffectFilter && (
        <Popover
          isOpen={isEffectDropdownOpen}
          setIsOpen={setIsEffectDropdownOpen}
          label={t('Portfolio.Position effect')}
          content={
            <PopoverCard>
              <PopoverList>
                {effectOptions.map((option) => {
                  const isActive = option.id === searchFilters?.positionEffect

                  return (
                    <ListItem
                      key={option.id}
                      onClick={() => handleEffectSelect(option.id)}
                      active={isActive}
                      endSlot={
                        isActive
                          ? {
                              icon: {
                                icon: { icon: CircleCheckIcon, color: 'text-purple-high-em' },
                              },
                            }
                          : undefined
                      }
                    >
                      {option.name}
                    </ListItem>
                  )
                })}
              </PopoverList>
            </PopoverCard>
          }
        >
          <Button.Secondary
            size='md'
            iconRight={ChevronIcon.Down}
            onClick={() => setIsEffectDropdownOpen(!isEffectDropdownOpen)}
            endDecorator={
              searchFilters?.positionEffect && (
                <Chip
                  selected
                  size='xs'
                  onDelete={(event) => {
                    event.stopPropagation()
                    event.preventDefault()
                    setSearchFilters?.({ positionEffect: undefined })
                  }}
                >
                  {'1'}
                </Chip>
              )
            }
          >
            {t('Portfolio.Position effect')}
          </Button.Secondary>
        </Popover>
      )}
    </div>
  )
}
